import { faArrowRotateRight, faUndo, faExpand, faCompress, faCheck, faXmark, faSearchPlus, faCamera } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, CircularProgress, Typography } from "@mui/material";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useGaiaMapContext } from "../hooks/useGaiaMapContext";
import {
    PanDrawButton,
    PointDrawButton,
    PolylineDrawButton,
    PolygonDrawButton,
    SubtractDrawButton,
    DeleteDrawButton,
    CloneDrawButton,
    CircleDrawButton,
    RulerDrawButton,
    SelectDrawButton,
    DeselectDrawButton,
    InspectDrawButton,
    EditDrawButton,
} from "./GaiaMapControls";
import { GAIA_DRAW_MODE, EnabledToolTypes, GaiaMapProps } from "../GaiaMap";

interface ToolItem {
    Component: React.ComponentType;
    label: string;
    mode: typeof GAIA_DRAW_MODE[keyof typeof GAIA_DRAW_MODE];
    toolType: EnabledToolTypes;
}

type GaiaMapRibbonProps = Pick<GaiaMapProps, "customRibbonButtons"> & {
    showSnapshotButton?: boolean;
};

export const GaiaMapRibbon = memo((props: GaiaMapRibbonProps) => {
    const { customRibbonButtons, showSnapshotButton = true } = props;
    const {
        canUndo,
        canRedo,
        onUndo,
        onRedo,
        drawMode,
        setDrawMode,
        acceptDrawing,
        cancelDrawing,
        hasGeometry,
        onClone,
        center,
        centerMap,
        readOnly,
        enabledToolTypes,
        disableUndoRedo,
        disableAcceptDrawing,
        getFeatureInfo,
        wmsUrl,
        activatePrintPreview,
        printLoading,
        onMapBoxSizeRequested,
        mapBoxSize,
        isFullScreen,
        setIsFullScreen,
    } = useGaiaMapContext();
    const isDrawing = drawMode !== null;
    const hasCloneFunction = typeof onClone === "function";
    const acceptDrawingMode = [GAIA_DRAW_MODE.POLYGON, GAIA_DRAW_MODE.POLYLINE, GAIA_DRAW_MODE.EDIT];
    const showAcceptDrawing = isDrawing && !disableAcceptDrawing && acceptDrawingMode.includes(drawMode as any);
    const toggleFullScreen = useCallback(() => {
        const mapContainer = document.querySelector(".gaia__container");

        if (!isFullScreen) {
            if (mapContainer && mapContainer.requestFullscreen) {
                mapContainer.requestFullscreen().catch((err) => {
                    console.warn(`Error attempting to enable fullscreen: ${err.message}`);
                });
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch((err) => {
                    console.warn(`Error attempting to exit fullscreen: ${err.message}`);
                });
            }
        }

        setIsFullScreen(!isFullScreen);
    }, [isFullScreen, setIsFullScreen]);

    const takeSnapshot = useCallback(async () => {
        if (!activatePrintPreview) {
            console.log("Print Preview not available");
            return;
        }
        if (mapBoxSize == null && typeof onMapBoxSizeRequested === "function") {
            await onMapBoxSizeRequested();
        }
        activatePrintPreview();
    }, [activatePrintPreview, mapBoxSize, onMapBoxSizeRequested]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, [setIsFullScreen]);

    // Determine which tools to show based on enabledToolTypes
    const visibleTools = useMemo(() => {
        // Define all possible tools
        const allTools: (ToolItem | null)[] = [
            hasCloneFunction ? { Component: CloneDrawButton, label: "Clone", mode: GAIA_DRAW_MODE.CLONE, toolType: "polygon" } : null,
            { Component: PointDrawButton, label: "Point", mode: GAIA_DRAW_MODE.MARKER, toolType: "point" },
            { Component: PolylineDrawButton, label: "Polyline", mode: GAIA_DRAW_MODE.POLYLINE, toolType: "polyline" },
            { Component: PolygonDrawButton, label: "Polygon", mode: GAIA_DRAW_MODE.POLYGON, toolType: "polygon" },
            { Component: SubtractDrawButton, label: "Subtract", mode: GAIA_DRAW_MODE.SUBTRACT, toolType: "polygon" },
            { Component: CircleDrawButton, label: "Circle", mode: GAIA_DRAW_MODE.CIRCLE, toolType: "circle" },
            { Component: DeleteDrawButton, label: "Delete", mode: GAIA_DRAW_MODE.DELETE, toolType: "delete" },
            { Component: SelectDrawButton, label: "Select", mode: GAIA_DRAW_MODE.SELECT, toolType: "selection" },
            { Component: DeselectDrawButton, label: "Deselect", mode: GAIA_DRAW_MODE.DESELECT, toolType: "selection" },
            { Component: EditDrawButton, label: "Edit", mode: GAIA_DRAW_MODE.EDIT, toolType: "edit" },
        ];

        // If no enabledToolTypes specified, show none tool
        if (!enabledToolTypes || enabledToolTypes.length === 0) {
            return [];
        }

        // Filter tools based on enabledToolTypes
        return allTools.filter(
            (tool): tool is ToolItem =>
                tool !== null &&
                (enabledToolTypes.includes(tool.toolType) ||
                    // Special case for Clone tool when "polygon" is enabled and onClone exists
                    (tool.mode === GAIA_DRAW_MODE.CLONE && enabledToolTypes.includes("polygon") && hasCloneFunction))
        );
    }, [enabledToolTypes, hasCloneFunction]);
    const rightButtons = useMemo(() => {
        const undoButton = { icon: faUndo, label: "Undo", onClick: onUndo, disabled: readOnly || !canUndo, loading: false };
        const redoButton = { icon: faArrowRotateRight, label: "Redo", onClick: onRedo, disabled: readOnly || !canRedo, loading: false };
        const snapshotButton = { icon: faCamera, label: "Take Snapshot", onClick: takeSnapshot, disabled: false, loading: printLoading };
        const fullScreenButton = { icon: isFullScreen ? faCompress : faExpand, label: "Full Screen", onClick: toggleFullScreen, disabled: false, loading: false };
        const buttons = [fullScreenButton];
        if (showSnapshotButton) {
            buttons.unshift(snapshotButton);
        }
        if (!disableUndoRedo && !readOnly && drawMode !== GAIA_DRAW_MODE.EDIT) {
            buttons.unshift(undoButton, redoButton);
        }
        return buttons;
    }, [onUndo, readOnly, canUndo, onRedo, canRedo, takeSnapshot, printLoading, isFullScreen, toggleFullScreen, showSnapshotButton, disableUndoRedo]);
    const acceptDrawingButtons = useMemo(() => {
        return [
            { icon: faCheck, label: "Accept", onClick: acceptDrawing, disabled: !hasGeometry },
            { icon: faXmark, label: "Cancel", onClick: cancelDrawing, disabled: !hasGeometry },
        ];
    }, [acceptDrawing, cancelDrawing, hasGeometry]);

    return (
        <Box
            sx={{
                minHeight: "60px",
                height: "80px",
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
                borderRight: "1px solid rgba(0, 0, 0, 0.12)",
            }}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
        >
            <Box display="flex" gap={2} alignItems="center" padding={1}>
                <Box
                    className={`gaia-draw-control ${drawMode === null ? "selected" : ""}`}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    onClick={() => {
                        // If already in drawing mode and changing to pan, cancel the current drawing first
                        if (drawMode !== null && hasGeometry) {
                            cancelDrawing();
                        }
                        setDrawMode(null);
                    }}
                >
                    <PanDrawButton />
                    <Typography variant="caption">Pan</Typography>
                </Box>
                <Box
                    className="gaia-draw-control"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    onClick={() => {
                        if (hasGeometry) {
                            centerMap(null);
                        } else {
                            centerMap(center);
                        }
                    }}
                >
                    <button className="gaia-draw-button">
                        <FontAwesomeIcon icon={faSearchPlus} />
                    </button>
                    <Typography variant="caption">Zoom To</Typography>
                </Box>
                {getFeatureInfo && wmsUrl && (
                    <Box
                        className={`gaia-draw-control ${drawMode === GAIA_DRAW_MODE.INSPECT ? "selected" : ""}`}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        onClick={() => {
                            // If already in drawing mode and changing to inspect, cancel the current drawing first
                            if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.INSPECT && hasGeometry) {
                                cancelDrawing();
                            }
                            setDrawMode(GAIA_DRAW_MODE.INSPECT);
                        }}
                    >
                        <InspectDrawButton />
                        <Typography variant="caption">Inspect</Typography>
                    </Box>
                )}
                <Box
                    className={`gaia-draw-control ${drawMode === GAIA_DRAW_MODE.RULER ? "selected" : ""}`}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    onClick={() => {
                        // If already in drawing mode and changing to ruler, cancel the current drawing first
                        if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.RULER && hasGeometry) {
                            cancelDrawing();
                        }
                        setDrawMode(GAIA_DRAW_MODE.RULER);
                    }}
                >
                    <RulerDrawButton />
                    <Typography variant="caption">Ruler</Typography>
                </Box>
                {visibleTools.map(({ Component, label, mode }, idx) => {
                    // Only disable tools if in readOnly mode
                    const disabled = readOnly;

                    return (
                        <Box
                            key={idx}
                            className={`gaia-draw-control ${drawMode === mode ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            onClick={() => {
                                if (!disabled) {
                                    // If already in drawing mode and changing to a different tool, cancel the current drawing first
                                    if (drawMode !== null && drawMode !== mode && hasGeometry) {
                                        cancelDrawing();
                                    }
                                    setDrawMode(mode);
                                }
                            }}
                        >
                            <Component />
                            <Typography variant="caption">{label}</Typography>
                        </Box>
                    );
                })}
            </Box>
            <Box display="inline-flex" gap={2} padding={1}>
                {customRibbonButtons}
                {showAcceptDrawing &&
                    acceptDrawingButtons.map(({ icon, label, onClick, disabled }, idx) => (
                        <Box
                            key={`draw-action-${idx}`}
                            className={`gaia-draw-control ${disabled ? "disabled" : ""}`}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            onClick={() => {
                                if (!disabled) {
                                    onClick();
                                }
                            }}
                        >
                            <button className="gaia-draw-button" disabled={disabled}>
                                <FontAwesomeIcon icon={icon} />
                            </button>
                            <Typography variant="caption">{label}</Typography>
                        </Box>
                    ))}
                {rightButtons.map(({ icon, label, onClick, disabled, loading }, idx) => (
                    <Box
                        key={`common-action-${idx}`}
                        className={`gaia-draw-control ${disabled || loading ? "disabled" : ""}`}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        onClick={() => {
                            if (!disabled && !loading) {
                                onClick();
                            }
                        }}
                    >
                        <button className="gaia-draw-button">{loading ? <CircularProgress size="18px" /> : <FontAwesomeIcon icon={icon} />}</button>
                        <Typography variant="caption">{label}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
});
